import React, { useState } from 'react';
import { SearchCheck, Check, ArrowRight, FileText, Search, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import RegisterPopup from '../components/RegisterPopup';
import ServiceProcessSection from '../components/ServiceProcessSection';
import ServiceFAQSection from '../components/ServiceFAQSection';
import SEO from '../components/SEO';

const TrademarkSearch = () => {
  const [showPopup, setShowPopup] = useState(false);

  const benefits = [
    'Identify potential trademark conflicts before filing',
    'Reduce risk of application rejection',
    'Save time and money on failed applications',
    'Comprehensive federal and state database searches',
    'Analysis of similar trademarks in your industry',
    'Expert recommendations on registration strategy'
  ];

  const searchIncludes = [
    'Federal trademark database (USPTO) search',
    'State trademark registrations',
    'Common law trademark usage',
    'Domain name availability check',
    'Social media handle availability',
    'Similar phonetic variations',
    'Visual similarity analysis',
    'Detailed conflict assessment report'
  ];

  const processSteps = [
    {
      icon: 'FileText',
      title: 'Initial Consultation',
      description: 'We discuss your trademark, industry, and business goals to tailor our search to your specific needs.'
    },
    {
      icon: 'Search',
      title: 'Comprehensive Database Search',
      description: 'Our team searches federal, state, and common law databases for potential conflicts and similar marks.'
    },
    {
      icon: 'Award',
      title: 'Detailed Report & Recommendations',
      description: 'You receive a comprehensive report with our findings and strategic recommendations for moving forward.'
    }
  ];

  const icons = {
    FileText: FileText,
    Search: Search,
    Award: Award
  };

  const searchFAQs = [
    {
      question: 'Why do I need a trademark search?',
      answer: 'A comprehensive trademark search helps identify potential conflicts with existing trademarks before you invest in filing. It minimizes rejection risk and helps you avoid costly rebranding issues or legal disputes down the road.'
    },
    {
      question: 'What does your trademark search include?',
      answer: 'Our search covers federal USPTO databases, state trademark registrations, common law usage, domain names, and social media handles. We also analyze phonetic and visual similarities to identify potential conflicts.'
    },
    {
      question: 'How long does a trademark search take?',
      answer: 'Most comprehensive trademark searches are completed within 2-3 business days. Expedited searches are available for urgent needs and can be completed within 24 hours.'
    },
    {
      question: 'What happens if conflicts are found?',
      answer: 'If our search reveals potential conflicts, we provide detailed analysis and recommendations. This may include suggesting modifications to your mark, pursuing registration in different classes, or advising on alternative strategies.'
    },
    {
      question: 'Is a trademark search required by law?',
      answer: 'While not legally required, conducting a trademark search is highly recommended by the USPTO and trademark attorneys. It significantly increases your chances of successful registration and protects your investment.'
    }
  ];

  return (
    <div>
      <SEO 
        title="Comprehensive Trademark Search Services - Conflict Detection | Trademark Flux"
        description="Thorough trademark search to identify potential conflicts before filing. Federal, state, and common law database searches. Reduce rejection risk with expert analysis."
        keywords="trademark search, comprehensive search, conflict detection, trademark availability, USPTO search, trademark clearance"
      />
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(37, 99, 235, 0.7)), url('https://images.unsplash.com/photo-1763038311036-6d18805537e5?q=80&w=2000')`,
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Trademark Comprehensive Search
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Minimize rejection risks with our thorough trademark search services. Know before you file and protect your investment.
            </p>
            <Button
              onClick={() => setShowPopup(true)}
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg font-semibold"
            >
              Order Your Search Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Search Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-aos="zoom-in" data-aos="fade-up">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Conduct a Trademark Search?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                A comprehensive trademark search is a critical first step before filing your trademark application. It helps identify potential conflicts with existing trademarks that could result in your application being rejected or lead to legal disputes down the road.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our expert search goes beyond basic database queries to analyze phonetic similarities, visual resemblances, and common law usage in your industry. This thorough approach helps you make informed decisions about your trademark strategy.
              </p>
            </div>
            <div className="bg-blue-600 rounded-lg p-8 text-white" data-aos="zoom-in" data-aos="fade-up">
              <SearchCheck className="h-16 w-16 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Avoid Costly Mistakes</h3>
              <p className="mb-6">
                Investing in a comprehensive search now can save you thousands in rebranding costs and legal fees later.
              </p>
              <Button
                onClick={() => setShowPopup(true)}
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                Get Your Search Report
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              What's Included in Our Search
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-aos="fade-up" data-aos="zoom-in">
              {searchIncludes.map((item, index) => (
                <div key={index} className="flex items-start bg-white p-6 rounded-lg shadow-md">
                  <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Benefits of Our Comprehensive Search
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-aos="fade-up" data-aos="zoom-in">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start bg-gray-50 p-6 rounded-lg" data-aos="zoom-in" data-aos-delay={index * 100}>
                  <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <ServiceProcessSection
        title="Our Simple 3-Step Process"
        subtitle="Get your comprehensive trademark search in three easy steps"
        steps={processSteps}
        icons={icons}
      />

      {/* FAQ Section */}
      <ServiceFAQSection
        faqs={searchFAQs}
        imageSrc="https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=800"
        onContactClick={() => setShowPopup(true)}
      />

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Start Your Trademark Search Today
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Get peace of mind with our comprehensive trademark search before you invest in your brand registration.
          </p>
          <Button
            onClick={() => setShowPopup(true)}
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg"
          >
            Order Search Report Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {showPopup && <RegisterPopup onClose={() => setShowPopup(false)} />}
    </div>
  );
};

export default TrademarkSearch;